<script lang="ts">
  /**
   * The names of the people playing on one device (1.7.0): a field per
   * seat, Add a player, and a way to remove one, between `min` and `max`.
   * For a setup page (a `PickerPage` section, under the game's own
   * `SectionLabel`) or a names sheet in the game's menu.
   *
   * - **Controlled.** `value` is the text of each field as typed, one per
   *   seat; every change (a keystroke, an add, a remove) calls `onchange`
   *   with the whole next list. A blank field is fine: it plays as its
   *   seat's default name, which it shows as its placeholder ("Player 2",
   *   `defaultName`). The game stores `resolvePlayerNames(value)`.
   * - **The first seat may be prefilled**: pass the name in `value[0]` (the
   *   device's own player). Nothing is locked; it is a field like the rest.
   * - **Validation, in the game's words.** Two seats may not share a name
   *   once resolved (compared case-insensitively, NFKC: `playerNameProblems`).
   *   The later seat is marked (`aria-invalid`, a danger outline) and
   *   `duplicateMessage` is written under it, politely announced. The game
   *   disables its Start while `playerNamesValid(value)` is false. A name
   *   longer than `maxLength` (default 16) cannot be typed.
   * - **Keyboard.** One fieldset named `label` (default the shell's
   *   "Players"; visually hidden, since the page's section label shows it).
   *   Each field is named by its seat ("Player 2", `fieldLabel`); Enter
   *   moves to the next field. Add puts focus in the new field; removing a
   *   seat puts it in the field that took its place (or the one before).
   *   Each remove button is named for the player ("Remove Noor").
   *
   * Test ids: `testid` (default `ui-player-names`) on the fieldset, and from
   * it `-field-<n>`, `-remove-<n>`, `-error-<n>` (n counts from 1, as the
   * seats do) and `-add`.
   */
  import { tick } from 'svelte';

  import { shellText } from '../i18n.svelte.js';
  import { defaultPlayerName, playerNameProblems, resolvePlayerNames } from '../localPlay.js';
  import Icon from './Icon.svelte';
  import TextButton from './TextButton.svelte';

  interface Props {
    readonly value: readonly string[];
    readonly onchange: (next: string[]) => void;
    readonly min?: number;
    readonly max?: number;
    readonly label?: string | undefined;
    /** A seat's own name for its field, 0-based. Default `defaultName`. */
    readonly fieldLabel?: ((index: number) => string) | undefined;
    /** What a blank seat plays as, 0-based. Default the shell's "Player {n}". */
    readonly defaultName?: ((index: number) => string) | undefined;
    readonly maxLength?: number;
    readonly addLabel?: string | undefined;
    /** The remove button's name, for the seat's name as it would play. */
    readonly removeLabel?: ((name: string, index: number) => string) | undefined;
    readonly duplicateMessage?: string | undefined;
    readonly disabled?: boolean;
    readonly testid?: string | undefined;
  }
  const {
    value,
    onchange,
    min = 2,
    max = 4,
    label,
    fieldLabel,
    defaultName = defaultPlayerName,
    maxLength = 16,
    addLabel,
    removeLabel,
    duplicateMessage,
    disabled = false,
    testid = 'ui-player-names',
  }: Props = $props();

  const baseId = $props.id();
  const inputs: (HTMLInputElement | null)[] = $state([]);

  const resolved = $derived(resolvePlayerNames(value, defaultName));
  const problems = $derived(playerNameProblems(value, defaultName));
  const canAdd = $derived(!disabled && value.length < max);
  const canRemove = $derived(!disabled && value.length > min);

  function nameOf(index: number): string {
    return (fieldLabel ?? defaultName)(index);
  }

  function update(index: number, text: string): void {
    const next = [...value];
    next[index] = text.slice(0, maxLength);
    onchange(next);
  }

  async function add(): Promise<void> {
    if (!canAdd) return;
    onchange([...value, '']);
    await tick();
    inputs[value.length - 1]?.focus();
  }

  async function remove(index: number): Promise<void> {
    if (!canRemove) return;
    onchange(value.filter((_, at) => at !== index));
    await tick();
    inputs[Math.min(index, value.length - 1)]?.focus();
  }

  function onKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'Enter') return;
    const next = inputs[index + 1];
    if (next === null || next === undefined) return;
    event.preventDefault();
    next.focus();
  }
</script>

<fieldset class="ui-player-names" {disabled} data-testid={testid}>
  <legend class="ui-player-names-legend">{label ?? shellText('playerNamesLabel')}</legend>
  {#each value as text, index (index)}
    {@const problem = problems[index] ?? null}
    {@const errorId = `${baseId}-error-${index}`}
    <div class="ui-player-names-row">
      <span class="ui-player-names-seat" aria-hidden="true">{index + 1}</span>
      <input
        bind:this={inputs[index]}
        class="ui-player-names-input"
        class:ui-player-names-input--invalid={problem !== null}
        type="text"
        value={text}
        placeholder={defaultName(index)}
        aria-label={nameOf(index)}
        aria-invalid={problem === null ? undefined : 'true'}
        aria-describedby={problem === null ? undefined : errorId}
        maxlength={maxLength}
        autocomplete="off"
        autocapitalize="words"
        autocorrect="off"
        spellcheck="false"
        enterkeyhint={index + 1 < value.length ? 'next' : 'done'}
        data-testid="{testid}-field-{index + 1}"
        oninput={(event) => update(index, event.currentTarget.value)}
        onkeydown={(event) => onKeydown(event, index)}
      />
      {#if canRemove}
        <button
          type="button"
          class="ui-player-names-remove"
          aria-label={removeLabel?.(resolved[index] ?? nameOf(index), index) ??
            shellText('playerNamesRemove', { name: resolved[index] ?? nameOf(index) })}
          data-testid="{testid}-remove-{index + 1}"
          onclick={() => void remove(index)}
        >
          <Icon name="close" size={18} />
        </button>
      {/if}
    </div>
    <p class="ui-player-names-error" id={errorId} aria-live="polite">
      {#if problem !== null}<span data-testid="{testid}-error-{index + 1}"
          >{duplicateMessage ?? shellText('playerNamesDuplicate')}</span
        >{/if}
    </p>
  {/each}
  {#if canAdd}
    <div class="ui-player-names-add">
      <TextButton
        label={addLabel ?? shellText('playerNamesAdd')}
        align="start"
        onclick={() => void add()}
        testid="{testid}-add"
      >
        {#snippet icon()}<Icon name="plus" size={18} />{/snippet}
      </TextButton>
    </div>
  {/if}
</fieldset>

<style>
  .ui-player-names {
    margin: 0;
    padding: 0;
    border: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    font-family: var(--ui-font);
  }

  .ui-player-names-legend {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .ui-player-names-row {
    display: flex;
    align-items: center;
    gap: var(--ui-space-2);
  }

  .ui-player-names-seat {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--ui-surface-2);
    color: var(--ui-ink);
    font: var(--ui-text-label);
    font-weight: 800;
  }

  .ui-player-names-input {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 2.75rem;
    box-sizing: border-box;
    padding: 0.7rem 0.8rem;
    border: 1px solid var(--ui-line);
    border-radius: var(--ui-radius-sm);
    background: var(--ui-card);
    color: var(--ui-ink);
    font: var(--ui-text-body);
    -webkit-user-select: text;
    user-select: text;
  }

  .ui-player-names-input::placeholder {
    color: var(--ui-ink-2);
  }

  .ui-player-names-input:focus-visible {
    outline: 2px solid var(--ui-accent);
    outline-offset: 1px;
  }

  .ui-player-names-input--invalid {
    border-color: var(--ui-danger);
  }

  .ui-player-names-remove {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--ui-ink-2);
  }

  .ui-player-names-remove:focus-visible {
    outline: 2px solid var(--ui-accent);
    outline-offset: 1px;
  }

  /* Always in the page: a live region must be there before it speaks. */
  .ui-player-names-error {
    margin: 0.2rem 0 0.4rem 2.5rem;
    font: var(--ui-text-caption);
    color: var(--ui-danger);
  }

  .ui-player-names-add {
    display: flex;
  }

  .ui-player-names:disabled {
    opacity: 0.6;
  }
</style>
